import Formsy from 'formsy-react';
import React, { ForwardedRef, forwardRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { countries, allStates } from '../../mock/Address.constants';
import DropdownContainer from './fields/Dropdown';
import Input from './fields/Input';
import ParsedMarkdown from './ParsedMarkdown';
import Asset from './Asset';
import Link from './LinkAdapter';
import { CountryCode } from '../../types/schema';
import { getIsMediumDevice } from '../../store/selectors/ui.selectors';
import { getNewsletterSubscribeResponse } from '../../store/selectors/user.selectors';
import { Nullable } from '../../types/common';
import paths from '../../paths';
import { default as styles } from '../../scss/components/NewsletterExpanded.module.scss';
import { NewsletterOnSubmitProps, NewsletterProps } from './Newsletter';

interface NewsletterExpandedProps extends NewsletterProps {
  onSubmit: (data: NewsletterOnSubmitProps) => void;
}

const NewsletterExpanded = (
  {
    header,
    richTextCopy,
    images,
    copy,
    onSubmit,
    nextApp,
    type,
  }: NewsletterExpandedProps,
  formRef: ForwardedRef<Nullable<Formsy>>,
) => {
  const newsletterSubscribeResponse: string = useSelector(
    getNewsletterSubscribeResponse,
  );

  const isTablet = useSelector(getIsMediumDevice);
  const [chosenCountry, setChosenCountry] = useState(CountryCode.US);
  const states = allStates[chosenCountry];
  const isNewsletterEmail = type === 'NewsletterEmail';

  return (
    <section className={styles['expanded-newsletter']}>
      {!isNewsletterEmail && (
      <div className={styles['expanded-newsletter__image']}>
        <Asset asset={images[0]} {...(isTablet && { customHeight: 300 })} />
      </div>
      )}
      <div className={isNewsletterEmail ? styles['expanded-newsletter__content-email'] : styles['expanded-newsletter__content']}>
        <h2 className={styles['expanded-newsletter__content-heading']}>
          {header}
        </h2>
        {copy && (
          <div
            className={styles['expanded-newsletter__content-copy']}
          >
            {copy}
          </div>
        )}
        <Formsy className="form-fancy" onValidSubmit={onSubmit} ref={formRef}>
          <div className="form-fancy-wrapper">
            {!isNewsletterEmail && (
              <Input
                name="firstName"
                label="First name"
                className="col-half col-half-mobile"
              />
            )}
            {!isNewsletterEmail && (
              <Input
                name="lastName"
                label="Last name"
                className="col-half col-half-mobile"
              />
            )}
            <Input
              label="Email Address*"
              autocapitalize="none"
              name="email"
              validations="isEmail"
              validationError="That doesn’t look like a valid email address"
              required
            />
            {!isNewsletterEmail && (
              <DropdownContainer
              name="country"
              validations="isExisty"
              validationError="Country required"
              label="Country*"
              className="select-options"
              required
              options={countries.map(({ title, value }) => ({
                label: title,
                value,
              }))}
              onChange={setChosenCountry}
              />
            )}
            {!isNewsletterEmail && (
              <Input
                label="City"
                autocapitalize="none"
                name="city"
                className="col-half col-half-mobile"
              />
            )}
            {!isNewsletterEmail && (
              <DropdownContainer
                name="state"
                label="State/Province"
                className="select-options col-half col-half-mobile"
                options={states.map(({ title, value }) => ({
                  label: title,
                  value,
                }))}
              />
            )}
            <div className="form-fancy">
              {richTextCopy?.markdown && (
                <ParsedMarkdown
                  className={styles['expanded-newsletter__content-rich-copy']}
                  options={{ forceBlock: true }}
                >
                  {richTextCopy.markdown}
                </ParsedMarkdown>
              )}
            </div>
            <div className="form-fancy__submit">
              <button
                type="submit"
                className="button btn-yellow"
              >
                {newsletterSubscribeResponse ?? 'SUBSCRIBE'}
              </button>

              <Link
                nextApp={nextApp}
                href={paths.TERMS}
                className="form-fancy__submit-terms"
              >
                Terms and conditions
              </Link>
            </div>
          </div>
        </Formsy>
      </div>
    </section>
  );
};

export default forwardRef(NewsletterExpanded);
